/**
 * Horizon 3.1 product-page extension. Runs before the native picker handler;
 * Horizon remains responsible for fetching, price, cart, media and variant events.
 */
export function chooseAvailableVariant(variants, selected, changedIndex) {
  const matches = (variant, length) => selected.slice(0, length).every((value, index) => variant.options[index] === value);
  const exact = variants.find((variant) => matches(variant, selected.length));
  if (exact?.available) return exact;

  // Never change the clicked option or options before it. Preserve subsequent
  // selections from left to right where a purchasable combination exists.
  for (let length = selected.length - 1; length > changedIndex; length--) {
    const candidate = variants.find((variant) => variant.available && matches(variant, length));
    if (candidate) return candidate;
  }
  return null; // Let Horizon show its normal unavailable/sold-out state.
}

class FoersterProductGeneral extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange, true);
    this.addEventListener('variant:selected', this.onVariantSelected);
    this.addEventListener('variant:update', this.onVariantUpdate);
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.mobileQuery.addEventListener('change', this.scheduleMetaPosition);
    this.observer = new MutationObserver(this.scheduleMetaPosition);
    this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-foerster-slot', 'data-meta-position', 'data-meta-option', 'data-mobile-meta', 'data-description-position'] });
    this.scheduleMetaPosition();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange, true);
    this.removeEventListener('variant:selected', this.onVariantSelected);
    this.removeEventListener('variant:update', this.onVariantUpdate);
    this.mobileQuery?.removeEventListener('change', this.scheduleMetaPosition);
    this.observer?.disconnect();
    cancelAnimationFrame(this.metaFrame);
  }

  scheduleMetaPosition = () => {
    cancelAnimationFrame(this.metaFrame);
    this.metaFrame = requestAnimationFrame(() => this.positionMeta());
  };

  isOwnVariantEvent(event) {
    return event.target.closest?.('foerster-product-general') === this;
  }

  onVariantSelected = (event) => {
    if (!this.isOwnVariantEvent(event)) return;
    const meta = this.querySelector('[data-foerster-meta]');
    const home = this.querySelector('[data-foerster-meta-home]');
    if (!meta || !home || !meta.closest('variant-picker')) return;
    // Horizon morphs the picker before emitting variant:update. Park the real
    // block outside it so its Liquid markup, editor identity and listeners survive.
    this.metaPending = true;
    home.hidden = true;
    home.append(meta);
  };

  onVariantUpdate = (event) => {
    if (!this.isOwnVariantEvent(event)) return;
    this.metaPending = false;
    this.positionMeta();
  };

  contentNode(role, selector) {
    this.contentNodes ??= {};
    // Keep a reference when deleting a position block in the editor removes its
    // relocated content too. The existing content can then return to its home.
    const current = this.querySelector(selector);
    if (current) this.contentNodes[role] = current;
    if (current) return current;
    const previous = this.contentNodes[role];
    const removedSlot = previous?.closest('[data-foerster-slot]');
    return removedSlot && !removedSlot.isConnected ? previous : null;
  }

  positionMeta() {
    if (!this.isConnected) return;
    const nodes = {
      meta: this.contentNode('meta', '[data-foerster-meta]'),
      description: this.contentNode('description', '.foerster-description'),
    };
    const homes = {
      meta: this.querySelector('[data-foerster-meta-home]'),
      description: this.querySelector('[data-foerster-description-home]'),
    };
    const details = this.querySelector('.product-details');
    const buy = details?.querySelector('.buy-buttons-block');
    // Horizon renders pickup availability as a sibling after the buy-buttons
    // span, with its module script between them. Keep them together when positioning content.
    let buyEnd = buy;
    if (buy) {
      let next = buy.nextElementSibling;
      while (next?.matches('script, style')) next = next.nextElementSibling;
      if (next?.matches('local-pickup')) buyEnd = next;
    }
    const groups = [];
    const plan = (node, target, kind = 'append') => {
      if (!target || target === node || node.contains(target)) return;
      let group = groups.find(g => g.target === target && g.kind === kind);
      if (!group) groups.push(group = { target, kind, nodes: [] });
      group.nodes.push(node);
    };
    for (const role of ['meta', 'description']) {
      const node = nodes[role], home = homes[role];
      if (!node || !home || (role === 'meta' && this.metaPending)) continue;
      home.hidden = false;
      const slot = [...this.querySelectorAll(`.product-details [data-foerster-slot="${role}"], [data-foerster-image-content] [data-foerster-slot="${role}"]`)]
        .find(el => !Object.values(nodes).some(n => n?.contains(el)) && !el.closest('variant-picker, product-form-component, .buy-buttons-block'));
      if (slot) { plan(node, slot); continue; }
      const mode = this.dataset[`${role}Position`] || (role === 'meta' ? 'legacy' : 'below_image');
      if (role === 'meta' && mode === 'after_option') {
        const form = details?.querySelector('variant-picker form');
        const options = form ? [...form.children].filter(el => el.matches('.variant-option')) : [];
        const index = Math.max(0, Math.min(options.length - 1, (Number(this.dataset.metaOption) || 1) - 1));
        if (options[index]) { plan(node, options[index], 'after'); continue; }
        if (buy) { plan(node, buy, 'before'); continue; }
      }
      if (mode === 'before_cart' || (role === 'meta' && mode === 'legacy' && this.mobileQuery.matches && this.dataset.mobileMeta === 'before_cart')) {
        if (buy) { plan(node, buy, 'before'); continue; }
      }
      if (mode === 'after_cart' && buyEnd) { plan(node, buyEnd, 'after'); continue; }
      if (mode === 'details_end') {
        const end = this.querySelector('[data-foerster-details-end]');
        if (end) { plan(node, end); continue; }
      }
      plan(node, home);
    }
    // Place content sharing an anchor as one ordered group. This avoids
    // competing moves and MutationObserver loops when positions are identical.
    for (const { target, kind, nodes: content } of groups) {
      if (kind === 'after') {
        let cursor = target.nextElementSibling;
        const matches = content.every(node => { const same = cursor === node; cursor = cursor?.nextElementSibling; return same; });
        if (!matches) target.after(...content);
      } else if (kind === 'before') {
        let cursor = target.previousElementSibling;
        const matches = [...content].reverse().every(node => { const same = cursor === node; cursor = cursor?.previousElementSibling; return same; });
        if (!matches) target.before(...content);
      } else {
        let cursor = target.lastElementChild;
        const matches = [...content].reverse().every(node => { const same = cursor === node; cursor = cursor?.previousElementSibling; return same; });
        if (!matches) target.append(...content);
      }
    }
  }

  onChange = (event) => {
    if (this.dataset.autoSelect !== 'true') return;
    const target = event.target;
    if (target.closest?.('[data-foerster-meta]')) return;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    const picker = target.closest('variant-picker');
    if (!picker || picker.closest('foerster-product-general') !== this) return;
    if (picker.dataset.productId !== this.dataset.productId) return;
    if (typeof picker.updateSelectedOption !== 'function') return;

    const selectedOption = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    if (!selectedOption?.dataset.optionValueId) return;
    // Combined listings can lead to a different product. Keep the native flow.
    const connectedUrl = selectedOption.dataset.connectedProductUrl;
    if (connectedUrl && connectedUrl.split('?')[0] !== picker.dataset.productUrl?.split('?')[0]) return;

    const groups = [...picker.querySelectorAll('.variant-option')];
    const changedIndex = groups.findIndex((group) => group.contains(target));
    if (changedIndex < 0) return;
    const selected = groups.map((group) => group.querySelector('select')?.value ?? group.querySelector('input:checked')?.value);
    if (selected.some((value) => value === undefined)) return;

    let data;
    try {
      data = JSON.parse(this.querySelector('[data-foerster-variants]')?.textContent || 'null');
    } catch {
      return;
    }
    // Never infer availability from an incomplete Liquid variant list.
    if (!Array.isArray(data?.variants) || data.total !== data.variants.length) return;
    const candidate = chooseAvailableVariant(data.variants, selected, changedIndex);
    if (!candidate) return;

    // Resolve all inputs before changing any of them; the native request must
    // always see a complete valid option combination.
    const controls = groups.map((group, index) => {
      const select = group.querySelector('select');
      if (select) {
        return [...select.options].some((option) => option.value === candidate.options[index]) ? select : null;
      }
      return [...group.querySelectorAll('input[type="radio"]')].find((input) => input.value === candidate.options[index]);
    });
    if (controls.some((control) => !control)) return;
    controls.forEach((control, index) => {
      if (control instanceof HTMLSelectElement) {
        if (control.value === candidate.options[index]) return;
        control.value = candidate.options[index];
      } else if (control.checked) {
        return;
      }
      picker.updateSelectedOption(control);
    });
    // The native handler uses this value for the browser URL. Option-value IDs
    // are read from the corrected controls for its single section request.
    selectedOption.dataset.variantId = String(candidate.id);
  };
}

if (!customElements.get('foerster-product-general')) {
  customElements.define('foerster-product-general', FoersterProductGeneral);
}
